import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useNavigation,
  useNavigate,
  useSearchParams,
} from "@remix-run/react";
import { getAuthUser, signUp } from "~/services/auth.server";
import React from "react";
import { GoogleSignInButton } from "~/components/auth/GoogleSignInButton";

export const meta: MetaFunction = () => {
  return [
    { title: "회원가입 - 슬로우다이브" },
    { name: "description", content: "새 계정을 만드세요" },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);
  if (user) {
    // 이미 로그인된 사용자는 redirect 파라미터가 있으면 해당 페이지로, 없으면 홈으로
    const url = new URL(request.url);
    const redirectTo = url.searchParams.get("redirect") || "/";
    return redirect(redirectTo);
  }
  return json({});
}

export async function action({ request }: ActionFunctionArgs) {
  console.log("Signup action called");

  try {
    const formData = await request.formData();
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;
    const name = formData.get("name") as string;

    console.log("Form data received:", {
      email,
      name,
      passwordLength: password?.length,
    });

    if (!email || !password || !confirmPassword || !name) {
      console.log("Missing required fields");
      return json({ error: "모든 필드를 입력해주세요." }, { status: 400 });
    }

    if (password !== confirmPassword) {
      console.log("Password mismatch");
      return json({ error: "비밀번호가 일치하지 않습니다." }, { status: 400 });
    }

    if (password.length < 6) {
      console.log("Password too short");
      return json(
        { error: "비밀번호는 최소 6자 이상이어야 합니다." },
        { status: 400 }
      );
    }

    console.log("Calling signUp function...");
    const result = await signUp(request, email, password, name);
    console.log("SignUp completed successfully");

    if (result.requiresEmailConfirmation) {
      return json({
        success: true,
        message: result.message,
        requiresEmailConfirmation: true,
      });
    }

    return json({
      success: true,
      message: result.message,
      requiresEmailConfirmation: false,
    });
  } catch (error) {
    console.error("Signup action error:", error);
    let errorMessage = "회원가입에 실패했습니다.";
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (message.includes("user already registered")) {
        errorMessage = "이미 등록된 이메일입니다.";
      } else if (message.includes("invalid email")) {
        errorMessage = "올바른 이메일 주소를 입력해주세요.";
      } else if (message.includes("password")) {
        errorMessage = "비밀번호는 최소 6자 이상이어야 합니다.";
      } else {
        errorMessage = error.message;
      }
    }
    return json({ error: errorMessage }, { status: 400 });
  }
}

export default function Signup() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSubmitting = navigation.state === "submitting";

  // Handle successful signup
  React.useEffect(() => {
    if (actionData && "success" in actionData && actionData.success) {
      const redirectTo = searchParams.get("redirect");
      const loginUrl = redirectTo
        ? `/login?message=${encodeURIComponent(
            actionData.message
          )}&redirect=${encodeURIComponent(redirectTo)}`
        : `/login?message=${encodeURIComponent(actionData.message)}`;
      navigate(loginUrl);
    }
  }, [actionData, navigate, searchParams]);

  return (
    <div className="min-h-[calc(100vh-8.5rem-298px)] flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            새 계정 만들기
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            또는{" "}
            <Link
              to={`/login${
                searchParams.get("redirect")
                  ? `?redirect=${encodeURIComponent(
                      searchParams.get("redirect")!
                    )}`
                  : ""
              }`}
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              기존 계정으로 로그인
            </Link>
          </p>
        </div>

        <Form className="mt-8 space-y-6" method="post">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                이름
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="이름을 입력하세요"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                이메일 주소
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="이메일 주소를 입력하세요"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                비밀번호
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="비밀번호 (최소 6자)"
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                비밀번호 확인
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 sm:text-sm"
                placeholder="비밀번호를 다시 입력하세요"
              />
            </div>
          </div>

          {actionData && "error" in actionData && actionData.error && (
            <div className="text-red-600 text-sm text-center dark:text-red-400">
              {actionData.error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-400 dark:hover:bg-blue-500 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "가입 중..." : "회원가입"}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                  또는
                </span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleSignInButton
                text="Google로 계속하기"
                redirectTo={searchParams.get("redirect") || undefined}
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
